import { RouteUtils } from "andculturecode-javascript-core";
import axios from "axios";
import { isEmpty } from "../generic-utils";
import { LocalStorageUtils } from "../local-storage-utils";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import { createLogger } from "../logger";

const log = createLogger("ServiceBase");

let authReadyPromise: Promise<User | null> | null = null;

const waitForAuth = (): Promise<User | null> => {
    const auth = getAuth();

    if (authReadyPromise) {
        return authReadyPromise;
    }

    if (auth.currentUser) {
        authReadyPromise = Promise.resolve(auth.currentUser);
        return authReadyPromise;
    }

    authReadyPromise = new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
        });
    });

    return authReadyPromise;
};

onAuthStateChanged(getAuth(), (user) => {
    if (user) {
        authReadyPromise = Promise.resolve(user);
    }
});

axios.interceptors.request.use(async (config) => {
    const user = await waitForAuth();
    if (user) {
        const token = await user.getIdToken();
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;
        log.error("API request failed", {
            exception: error,
            status: status,
            method: originalRequest?.method?.toUpperCase() ?? "?",
            url: originalRequest?.url ?? "?",
        });

        return Promise.reject(error);
    }
);


const { REACT_APP_API_URL } = process.env;

if (!REACT_APP_API_URL || REACT_APP_API_URL === "") {
    throw new Error("The REACT_APP_API_URL needs to be set in the .env file");
}

let host: string = REACT_APP_API_URL;

// ---------------------------------------------------------------------------------------------
// #region Public Functions
// ---------------------------------------------------------------------------------------------

const ServiceBase = {
    /**
     * Sets the base url used to make API calls
     *
     * @param baseUrl The base url to use for axios calls, do not end with a /
     */
    setBaseUrl(baseUrl: string) {
        host = baseUrl;
    },

    /**
     * Calls the Symmio API via the GET verb and returns a serialized object
     *
     * @param {string} resourceEndpoint The complete resource endpoint not including the base Url
     * @param {number | undefined} expiration The number in milliseconds that we should expire this entry from cache.
     * If not provided, this call will not be cached.
     * @returns The object type defined in TRecord
     */
    get<TRecord, TPathParams = undefined, TQueryParams = undefined>(
        resourceEndpoint: string,
        expiration: number | undefined = undefined
    ) {
        return async (pathParams?: TPathParams, queryParams?: TQueryParams) => {
            const endpointTransform = RouteUtils.getUrlFromPath(
                resourceEndpoint,
                pathParams,
                queryParams
            );

            // check cache
            if (expiration) {
                var cache = await LocalStorageUtils.get<TRecord>(
                    endpointTransform
                );
                if (cache) {
                    return cache;
                }
            }

            var response = await _get<TRecord, TPathParams, TQueryParams>(
                host + endpointTransform,
                pathParams,
                queryParams
            );

            // set cache
            if (expiration && !isEmpty(response)) {
                LocalStorageUtils.set<TRecord>(
                    endpointTransform,
                    response,
                    expiration
                );
            }

            return response;
        };
    },

    /*
        TRecord: response type
    */
    list<TRecord, TQueryParams = null>(resourceEndpoint: string) {
        const url = `${host}${resourceEndpoint}`;
        return async (queryParams?: TQueryParams, headers?: Record<string, string>) =>
            await _list<TRecord>(url, null, queryParams, headers);
    },

    /*
        TRecord: response type
        TData: data type (defaults to TRecord)
    */
    post<TRecord, TData, TQueryParams = unknown>(resourceEndpoint: string) {
        const url = `${host}${resourceEndpoint}`;
        return async (data?: TData, queryParams?: TQueryParams, headers?: Record<string, string>) =>
            await _post<TRecord, TData>(url, data, undefined, queryParams, headers);
    },
};

// #endregion Public Functions

// ---------------------------------------------------------------------------------------------
// #region Private Functions
// ---------------------------------------------------------------------------------------------

const _get = async function <
    TRecord,
    TPathParams = undefined,
    TQueryParams = undefined
>(
    resourceEndpoint: string,
    pathParams?: TPathParams,
    queryParams?: TQueryParams
) {
    const result = await axios.get(resourceEndpoint, { timeout: 30000 });
    return result.data as TRecord;
};

const _list = async function <TRecord>(
    baseEndpoint: string,
    pathParams?: any,
    queryParams?: any,
    headers?: Record<string, string>
) {
    const url = RouteUtils.getUrlFromPath(
        baseEndpoint,
        pathParams,
        queryParams
    );
    const result = await axios.get(url, { timeout: 30000, headers });
    return result.data as TRecord;
};

const _post = async function <TRecord, TData>(
    resourceEndpoint: string,
    data?: TData,
    pathParams?: any,
    queryParams?: any,
    headers?: Record<string, string>
) {
    const url = RouteUtils.getUrlFromPath(
        resourceEndpoint,
        pathParams,
        queryParams
    );
    const result = await axios.post(url, data, { timeout: 30000, ...(headers ? { headers } : {}) });
    return result.data as TRecord;
};

// #endregion Private Functions

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export { ServiceBase };

// #endregion Exports
